import * as XLSX from "xlsx-js-style";
import { boardService, boardTaskService } from "../../api/taskBoardService";
import { buildAnalysis, computeAnalysis, enhanceAnalysisWorkbook } from "./qaAnalysis";

/**
 * End-of-sprint QA report (admins only), built live from the sprint's Done
 * tasks so it is always up to date:
 *   - Summary: points per developer — all tasks, then one section per squad.
 *     Each task = 1 point per developer; a task QA rejected at least once
 *     costs that developer 1 point, and a task that missed its deadline
 *     (even if finished later) costs 1 more.
 *   - Analysis: a dashboard sheet (qaAnalysis.js).
 *   - All Tasks, then one sheet per squad: every Done task with its
 *     developers and whether QA rejected it.
 */

const COLORS = {
  title: "1F4E79",
  header: "2F4F7F",
  squadHeaders: ["3E6FA6", "B8733A", "4E8A5B", "7A5BA6"],
  zebra: "F2F6FB",
  total: "DCE3F0",
  amber: "FFF2CC",
  red: "F8D7D4",
  note: "595959",
};

const border = {
  top: { style: "thin", color: { rgb: "C9D2E0" } },
  bottom: { style: "thin", color: { rgb: "C9D2E0" } },
  left: { style: "thin", color: { rgb: "C9D2E0" } },
  right: { style: "thin", color: { rgb: "C9D2E0" } },
};
const font = (extra = {}) => ({ name: "Calibri", sz: 11, ...extra });
const STYLE = {
  title: { font: font({ sz: 16, bold: true, color: { rgb: COLORS.title } }) },
  note: { font: font({ sz: 10, italic: true, color: { rgb: COLORS.note } }) },
  section: { font: font({ sz: 13, bold: true, color: { rgb: COLORS.title } }) },
  header: (rgb) => ({
    font: font({ bold: true, color: { rgb: "FFFFFF" } }),
    fill: { patternType: "solid", fgColor: { rgb } },
    alignment: { horizontal: "center", vertical: "center" },
    border,
  }),
  cell: (fill, extra = {}) => ({
    font: font(extra.font || {}),
    fill: fill ? { patternType: "solid", fgColor: { rgb: fill } } : undefined,
    alignment: { vertical: "center", wrapText: true, ...(extra.alignment || {}) },
    border,
  }),
};

const nameOf = (a) => a?.fullNameEn || a?.nickname || "Unknown";
const devsOf = (t) => (t.assignees?.length ? t.assignees.map(nameOf) : ["Unassigned"]);
const rejected = (t) => (t.qaRejectedCount || 0) > 0;
const late = (t) => Boolean(t.deadlineMissed);
const lateLabel = (t) => (late(t) ? `Late ${t.daysLate || 1}d (−1)` : "");
const rejectionLabel = (t) =>
  rejected(t) ? `Yes${t.qaRejectedCount > 1 ? ` ×${t.qaRejectedCount}` : ""} (−1)` : "";

/** Points per developer for a list of tasks, highest score first. */
export const scoreByDeveloper = (tasks) => {
  const rows = new Map();
  for (const t of tasks) {
    for (const dev of devsOf(t)) {
      const r = rows.get(dev) || { dev, worked: 0, penalty: 0, latePenalty: 0 };
      r.worked += 1;
      if (rejected(t)) r.penalty -= 1;
      if (late(t)) r.latePenalty -= 1;
      rows.set(dev, r);
    }
  }
  return [...rows.values()]
    .map((r) => ({ ...r, net: r.worked + r.penalty + r.latePenalty }))
    .sort((a, b) => (a.dev === "Unassigned") - (b.dev === "Unassigned") || b.net - a.net || a.dev.localeCompare(b.dev));
};

/** Small builder: write rows with styles into a sheet. */
const sheetBuilder = () => {
  const ws = {};
  const merges = [];
  let row = 0;
  const put = (r, c, v, s) => {
    ws[XLSX.utils.encode_cell({ r, c })] = { v, t: typeof v === "number" ? "n" : "s", s };
  };
  return {
    title(text, width) {
      put(row, 0, text, STYLE.title);
      merges.push({ s: { r: row, c: 0 }, e: { r: row, c: width - 1 } });
      row += 1;
    },
    note(text, width) {
      put(row, 0, text, STYLE.note);
      merges.push({ s: { r: row, c: 0 }, e: { r: row, c: width - 1 } });
      row += 1;
    },
    section(text) {
      put(row, 0, text, STYLE.section);
      row += 1;
    },
    header(cols, color) {
      cols.forEach((c, i) => put(row, i, c, STYLE.header(color)));
      row += 1;
    },
    line(values, styles) {
      values.forEach((v, i) => put(row, i, v, styles[i]));
      row += 1;
    },
    blank() {
      row += 1;
    },
    finish(widths) {
      ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: Math.max(row - 1, 0), c: widths.length - 1 } });
      ws["!cols"] = widths.map((wch) => ({ wch }));
      ws["!merges"] = merges;
      return ws;
    },
  };
};

const scoreSection = (b, title, tasks, color) => {
  const rows = scoreByDeveloper(tasks);
  b.section(title);
  b.header(["Developer", "Tasks Worked On", "QA Rejection Penalty", "Deadline Penalty", "Net Score"], color);
  rows.forEach((r, i) => {
    const zebra = i % 2 ? null : COLORS.zebra;
    const center = { alignment: { horizontal: "center" } };
    b.line(
      [r.dev, r.worked, r.penalty, r.latePenalty, r.net],
      [
        STYLE.cell(zebra),
        STYLE.cell(zebra, center),
        STYLE.cell(r.penalty ? COLORS.red : zebra, center),
        STYLE.cell(r.latePenalty ? COLORS.red : zebra, center),
        STYLE.cell(zebra, { ...center, font: { bold: true } }),
      ],
    );
  });
  const sum = (k) => rows.reduce((a, r) => a + r[k], 0);
  const bold = { font: { bold: true }, alignment: { horizontal: "center" } };
  b.line(
    ["TOTAL", sum("worked"), sum("penalty"), sum("latePenalty"), sum("net")],
    [
      STYLE.cell(COLORS.total, { font: { bold: true } }),
      STYLE.cell(COLORS.total, bold),
      STYLE.cell(COLORS.total, bold),
      STYLE.cell(COLORS.total, bold),
      STYLE.cell(COLORS.total, bold),
    ],
  );
  b.blank();
};

const tasksSheet = (title, tasks, color) => {
  const b = sheetBuilder();
  b.title(title, 5);
  b.note("Amber = multiple developers. Red = rejected by QA at least once (−1) / finished after the due date (−1).", 5);
  b.blank();
  b.header(["#", "Task", "Developer(s)", "QA Rejected?", "Late?"], color);
  tasks.forEach((t, i) => {
    const devs = devsOf(t);
    const fill = devs.length > 1 ? COLORS.amber : i % 2 ? null : COLORS.zebra;
    b.line(
      [i + 1, t.title, devs.join(", "), rejectionLabel(t), lateLabel(t)],
      [
        STYLE.cell(fill, { alignment: { horizontal: "center" } }),
        STYLE.cell(fill),
        STYLE.cell(fill),
        STYLE.cell(rejected(t) ? COLORS.red : fill, { alignment: { horizontal: "center" } }),
        STYLE.cell(late(t) ? COLORS.red : fill, { alignment: { horizontal: "center" } }),
      ],
    );
  });
  return b.finish([6, 70, 34, 16, 14]);
};

const safeSheetName = (name, used) => {
  let base = name.replace(/[\\/?*[\]:]/g, " ").slice(0, 31).trim() || "Sheet";
  let n = 2;
  let out = base;
  while (used.has(out)) out = `${base.slice(0, 28)} ${n++}`;
  used.add(out);
  return out;
};

/** Fetch the sprint's Done tasks and download the report workbook. */
export const downloadSprintReport = async (sprint) => {
  const [tasksRes, optionsRes] = await Promise.all([
    boardTaskService.list({ sprint: sprint._id, status: "done" }),
    boardService.getOptions(),
  ]);
  const tasks = [...(tasksRes.data || [])].sort((a, b) => (a.number || 0) - (b.number || 0));
  const squads = (optionsRes.data?.squads || []).filter((s) => tasks.some((t) => t.squad === s._id));
  const bySquad = squads.map((s) => ({ squad: s, tasks: tasks.filter((t) => t.squad === s._id) }));

  const label = sprint.name;
  const wb = XLSX.utils.book_new();
  const used = new Set();

  const summary = sheetBuilder();
  summary.title(`${label} QA Done — Tasks by Developer`, 5);
  summary.note(
    "Each task = 1 point per developer. A task rejected by QA costs 1 point; a task finished after its due date costs 1 point.",
    5,
  );
  summary.blank();
  scoreSection(summary, "All tasks", tasks, COLORS.header);
  bySquad.forEach(({ squad, tasks: list }, i) =>
    scoreSection(summary, `${squad.name} only`, list, COLORS.squadHeaders[i % COLORS.squadHeaders.length]),
  );
  XLSX.utils.book_append_sheet(wb, summary.finish([28, 18, 22, 18, 14]), safeSheetName("Summary", used));

  // Dashboard-style analysis (KPIs, developers, squads, collaboration, insights).
  const analysis = buildAnalysis(label, computeAnalysis(tasks, optionsRes.data?.squads || []));
  XLSX.utils.book_append_sheet(wb, analysis.ws, safeSheetName("Analysis", used));

  XLSX.utils.book_append_sheet(wb, tasksSheet(`${label} QA Done — All Tasks`, tasks, COLORS.header), safeSheetName("All Tasks", used));
  bySquad.forEach(({ squad, tasks: list }, i) =>
    XLSX.utils.book_append_sheet(
      wb,
      tasksSheet(`${label} QA Done — ${squad.name} Tasks`, list, COLORS.squadHeaders[i % COLORS.squadHeaders.length]),
      safeSheetName(squad.name, used),
    ),
  );

  const file = `${label.replace(/[^\w-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "")}_QA-Report.xlsx`;
  // SheetJS cannot write charts / data bars: they are added to the file afterwards
  // (Analysis is the 2nd sheet, after Summary).
  const bytes = await enhanceAnalysisWorkbook(XLSX.write(wb, { type: "array", bookType: "xlsx" }), analysis.layout, 2);
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = file;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
